import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Center, useToast,
  Button, HStack, useColorModeValue, Badge, Text, Flex, Tag, Icon,
  InputGroup, InputLeftElement, Input, Alert, AlertIcon, AlertTitle, AlertDescription,
  VStack
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, TimeIcon, SearchIcon, QuestionIcon } from '@chakra-ui/icons';
import { FiEye, FiMessageCircle, FiAlertCircle, FiFilter, FiRefreshCw } from 'react-icons/fi';
import AdminChatModal from '../AdminChat/AdminChatModal';
import RejectDetailsModal from './components/RejectDetailsModal';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const RejectConfirmations = () => {
  const [rejectConfirmations, setRejectConfirmations] = useState([]);
  const [filteredConfirmations, setFilteredConfirmations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRejectConfirmation, setSelectedRejectConfirmation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const toast = useToast();

  // Helper to get user display name
  const getUserDisplayName = (user) => {
    if (!user) return 'N/A';
    
    // If user is an object with name property
    if (typeof user === 'object' && user !== null) {
      if (user.name) return user.name;
      if (user.userName) return user.userName;
      if (user.fullName) return user.fullName;
      
      // Try other common name properties
      const nameProps = ['displayName', 'username', 'email', 'phone'];
      for (const prop of nameProps) {
        if (user[prop]) return user[prop];
      }
    }
    
    // If user is a string (probably an ID or name)
    if (typeof user === 'string') {
      return user;
    }
    
    return 'N/A';
  };
  
  // Color scheme
  const tableBorderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const filterBg = useColorModeValue('white', 'gray.800');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch reject confirmations on component mount
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchRejectConfirmations();
  }, []);

  // Filter confirmations when data, search term, or status filter changes
  useEffect(() => {
    applyFilters();
  }, [rejectConfirmations, searchTerm, statusFilter]);

  // Function to apply filters
  const applyFilters = () => {
    let result = [...rejectConfirmations];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(item => {
        // Check ID
        if (item._id && item._id.toLowerCase().includes(searchLower)) return true;
        
        // Check service description
        if (item.serviceId?.description && 
            item.serviceId.description.toLowerCase().includes(searchLower)) return true;
            
        // Check user name using our helper
        const userName = getUserDisplayName(item.userId).toLowerCase();
        if (userName !== 'n/a' && userName.includes(searchLower)) return true;
        
        // Check reason
        if (item.reason && item.reason.toLowerCase().includes(searchLower)) return true;
        
        return false;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'active':
          result = result.filter(item => !item.actionTaken); // All non-completed
          break;
        case 'pending':
          result = result.filter(item => !item.inProgress && !item.actionTaken);
          break;
        case 'inProgress':
          result = result.filter(item => item.inProgress && !item.actionTaken && !item.pendingConfirmation);
          break;
        case 'pendingConfirmation':
          result = result.filter(item => item.pendingConfirmation && !item.actionTaken);
          break;
        case 'completed':
          result = result.filter(item => item.actionTaken);
          break;
        default:
          break;
      }
    }

    setFilteredConfirmations(result);
  };

  // Function to fetch rejection confirmations list
  const fetchRejectConfirmations = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/rejectconfirmation/web/getallreject`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch rejection confirmations');
      }

      const data = await response.json();
      
      setRejectConfirmations(data.data || []);
      setFilteredConfirmations(data.data || []);
    } catch (error) {
      setError(error.message);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar confirmações de rejeição',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('active');
  };

  // Fetch rejection confirmation details by ID
  const fetchRejectConfirmationById = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/rejectconfirmation/web/getrejectId/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch rejection confirmation details');
      }

      const data = await response.json();

      // Auto-change to "Em Atendimento" if still in "Aguardando"
      if (data.success && data.data && !data.data.inProgress && !data.data.actionTaken) {
        // Call the PUT endpoint to update status
        await fetch(
          `${API_BASE_URL}api/rejectconfirmation/web/confirmActionTaken/${id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ inProgress: true }),
          }
        );

        // Update local state
        const updatedData = { ...data.data, inProgress: true };
        setSelectedRejectConfirmation(updatedData);

        // Update the list as well
        const updatedConfirmations = rejectConfirmations.map((rc) =>
          rc._id === id ? { ...rc, inProgress: true } : rc
        );
        setRejectConfirmations(updatedConfirmations);
      } else {
        setSelectedRejectConfirmation(data.data);
      }

      setIsModalOpen(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar detalhes da confirmação de rejeição',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle open chat modal
  const openAdminChat = (userId) => {
    setCurrentUserId(userId);
    setIsChatModalOpen(true);
  };

  // Update status (Action Taken)
  const handleActionTaken = async (rejectConfirmationId) => {
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');

      // Send request to update backend
      const response = await fetch(
        `${API_BASE_URL}api/rejectconfirmation/web/confirmActionTaken/${rejectConfirmationId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        // USE BACKEND RESPONSE DATA instead of assuming values
        const updatedFields = {
          inProgress: result.data.inProgress,
          actionTaken: result.data.actionTaken,
          pendingConfirmation: result.data.pendingConfirmation
        };

        // Update the local state with actual backend data
        const updatedConfirmations = rejectConfirmations.map((rc) =>
          rc._id === rejectConfirmationId ? { ...rc, ...updatedFields } : rc
        );
        setRejectConfirmations(updatedConfirmations);

        // Also update selectedRejectConfirmation if it's the one being updated
        if (selectedRejectConfirmation && selectedRejectConfirmation._id === rejectConfirmationId) {
          setSelectedRejectConfirmation({
            ...selectedRejectConfirmation,
            ...updatedFields
          });
        }

        // Show appropriate message based on state
        if (result.pendingConfirmation) {
          toast({
            title: 'Aguardando Confirmação',
            description: 'Mensagem de confirmação enviada ao usuário',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Sucesso',
            description: result.message || 'Status atualizado com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Info',
          description: result.message || 'Não foi possível atualizar',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao atualizar status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Count confirmations by status
  const getCounts = () => {
    const pending = rejectConfirmations.filter(item => !item.inProgress && !item.actionTaken).length;
    const inProgress = rejectConfirmations.filter(item => item.inProgress && !item.actionTaken && !item.pendingConfirmation).length;
    const pendingConfirmation = rejectConfirmations.filter(item => item.pendingConfirmation && !item.actionTaken).length;
    const completed = rejectConfirmations.filter(item => item.actionTaken).length;

    return { pending, inProgress, pendingConfirmation, completed, total: rejectConfirmations.length };
  };
  
  const counts = getCounts();
  
  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">Lista de Recusas de Serviço</Heading>
        <Tag bg="#4bedffff" color="black" size="lg" borderRadius="full">
          <Flex align="center">
            <Icon as={FiAlertCircle} mr={2} />
            <Text>{rejectConfirmations.length} Recusas</Text>
          </Flex>
        </Tag>
      </Flex>

      {/* Status Summary */}
      <Flex 
        mb={4} 
        gap={3} 
        flexWrap="wrap"
        bg={filterBg}
        p={3}
        borderRadius="md"
        borderWidth="1px"
        borderColor={filterBorderColor}
      >
        <Button 
          leftIcon={<WarningIcon />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'active' : 'pending')}
        >
          Aguardando ({counts.pending})
        </Button>
        
        <Button
          leftIcon={<TimeIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'inProgress' ? 'active' : 'inProgress')}
        >
          Em Andamento ({counts.inProgress})
        </Button>

        <Button
          leftIcon={<QuestionIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'pendingConfirmation' ? 'active' : 'pendingConfirmation')}
        >
          Aguardando Confirmação ({counts.pendingConfirmation})
        </Button>

        <Button
          leftIcon={<CheckIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'completed' ? 'active' : 'completed')}
        >
          Concluído ({counts.completed})
        </Button>
        
        <Button 
          leftIcon={<FiRefreshCw />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={resetFilters}
          ml="auto"
        >
          Limpar Filtros
        </Button>
      </Flex>

      {/* Search Box */}
      <Flex mb={4}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input 
            placeholder="Buscar por ID, serviço, razão..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="md"
          />
        </InputGroup>
      </Flex>

      {loading && !selectedRejectConfirmation ? (
        <Center h="50vh">
          <Spinner size="xl" thickness="4px" color="red.500" />
        </Center>
      ) : error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle mr={2}>Erro ao carregar dados</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
          <Button ml="auto" colorScheme="red" size="sm" onClick={fetchRejectConfirmations}>
            Tentar novamente
          </Button>
        </Alert>
      ) : (
        <Box 
          overflowX="auto"
          borderWidth="1px"
          borderRadius="lg"
          borderColor={tableBorderColor}
        >
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>ID</Th>
                <Th>Serviço</Th>
                <Th>Usuário</Th>
                <Th>Razão</Th>
                <Th>Data</Th>
                <Th>Status</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredConfirmations.length === 0 ? (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={6}>
                    <Text color="gray.500">
                      {rejectConfirmations.length > 0 
                        ? 'Nenhuma recusa encontrada com os filtros aplicados' 
                        : 'Nenhuma recusa de serviço encontrada'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                filteredConfirmations.map((item) => (
                  <Tr 
                    key={item._id} 
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    <Td>
                      <Button
                        variant="link"
                        color="teal"
                        onClick={() => fetchRejectConfirmationById(item._id)}
                        isTruncated
                        maxWidth="150px"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        {item._id.substring(0, 8)}...
                      </Button>
                    </Td>
                    <Td isTruncated maxWidth="200px">
                      {item.serviceId?.description || 'N/A'}
                    </Td>
                    <Td>
                      {getUserDisplayName(item.userId)}
                    </Td>
                    <Td isTruncated maxWidth="200px">
                      {item.reason || 'N/A'}
                    </Td>
                    <Td>
                      {item.createdAt 
                        ? new Date(item.createdAt).toLocaleDateString() 
                        : 'N/A'
                      }
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          item.actionTaken
                            ? 'green'
                            : item.pendingConfirmation
                              ? 'orange'
                              : item.inProgress
                                ? 'yellow'
                                : 'red'
                        }
                        px={2} py={1} borderRadius="full"
                      >
                        <Flex align="center">
                          <Icon
                            as={
                              item.actionTaken
                                ? CheckIcon
                                : item.pendingConfirmation
                                  ? QuestionIcon
                                  : item.inProgress
                                    ? TimeIcon
                                    : WarningIcon
                            }
                            mr={1}
                            boxSize="10px"
                          />
                          <Text>
                            {item.actionTaken
                              ? 'Concluído'
                              : item.pendingConfirmation
                                ? 'Aguardando Confirmação'
                                : item.inProgress
                                  ? 'Em Andamento'
                                  : 'Aguardando'
                            }
                          </Text>
                        </Flex>
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          leftIcon={<FiEye />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => fetchRejectConfirmationById(item._id)}
                        >
                          Detalhes
                        </Button>
                        <Button
                          leftIcon={<FiMessageCircle />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => openAdminChat(item.userId?._id || item.userId)}
                        >
                          Chat
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Use the modular RejectDetailsModal component */}
      <RejectDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        rejectionData={selectedRejectConfirmation}
        onActionTaken={handleActionTaken}
        onOpenChat={openAdminChat}
      />
      
      {/* Chat Modal */}
      <AdminChatModal
        userId={currentUserId}
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
      />
    </Box>
  );
};

export default RejectConfirmations;
